package fyi.moko.hotpl.data.service

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseAuthUserCollisionException
import dev.gitlive.firebase.auth.GoogleAuthProvider
import dev.gitlive.firebase.auth.OAuthProvider
import dev.gitlive.firebase.auth.auth
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.CredentialAlreadyInUseException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map

class FirebaseAuthService(
    emulatorHost: String? = null
) : AuthService {
    private val auth = dev.gitlive.firebase.Firebase.auth

    init {
        if (emulatorHost != null) {
            try {
                auth.useEmulator(emulatorHost, 9099)
            } catch (e: Exception) {
                // Ignore re-configuration errors
            }
        }
    }

    override val currentUserId: Flow<String?> = auth.authStateChanged.map { it?.uid }

    override suspend fun getCurrentUserId(): String? {
        return auth.currentUser?.uid
    }

    override suspend fun ensureSignedIn(): String {
        // The JS SDK restores the persisted session *asynchronously* (IndexedDB), so
        // currentUser is null for a moment after a page refresh even when a user is
        // signed in. Deciding on that null would mint a fresh anonymous user, permanently
        // replacing the persisted (e.g. Google) session — refresh demoted web to guest.
        // The first authStateChanged emission is the restoration result on every platform
        // (native SDKs restore synchronously and fire the listener immediately).
        val restored = auth.authStateChanged.first()
        if (restored != null) {
            return restored.uid
        }
        val result = auth.signInAnonymously()
        return result.user?.uid ?: throw IllegalStateException("Failed to sign in anonymously")
    }

    override suspend fun isAnonymous(): Boolean = auth.currentUser?.isAnonymous ?: true

    override suspend fun linkedEmail(): String? = auth.currentUser?.email

    override suspend fun displayName(): String? = auth.currentUser?.displayName?.ifBlank { null }

    override suspend fun signOut() {
        auth.signOut()
    }

    // [idToken] is the Google ID token (a JWT) obtained from the platform Sign-In flow.
    // [accessToken] is the OAuth access token: null on Android (Credential Manager only
    // surfaces an ID token, and GitLive/Firebase accepts an id-token-only Google credential
    // there) but non-null on iOS, where the native FIRGoogleAuthProvider credential rejects
    // a null accessToken with "accessToken must not be null".
    override suspend fun linkWithGoogle(idToken: String, accessToken: String?): Result<Unit> {
        val user = auth.currentUser
            ?: return Result.failure(IllegalStateException("No current user to link"))
        return try {
            user.linkWithCredential(GoogleAuthProvider.credential(idToken = idToken, accessToken = accessToken))
            Result.success(Unit)
        } catch (e: FirebaseAuthUserCollisionException) {
            // Credential already belongs to another account -> caller must switch via signInWithGoogle.
            Result.failure(CredentialAlreadyInUseException(cause = e))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signInWithGoogle(idToken: String, accessToken: String?): Result<String> {
        return try {
            val result = auth.signInWithCredential(GoogleAuthProvider.credential(idToken = idToken, accessToken = accessToken))
            val uid = result.user?.uid
                ?: return Result.failure(IllegalStateException("Sign-in returned no user"))
            Result.success(uid)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // rawNonce is the un-hashed nonce the platform Sign in with Apple flow generated before
    // SHA256-hashing it for the Apple request; Firebase hashes it again server-side and
    // compares against the hash embedded in idToken to reject replayed credentials.
    override suspend fun linkWithApple(idToken: String, rawNonce: String): Result<Unit> {
        val user = auth.currentUser
            ?: return Result.failure(IllegalStateException("No current user to link"))
        return try {
            user.linkWithCredential(OAuthProvider.credential(providerId = "apple.com", idToken = idToken, rawNonce = rawNonce))
            Result.success(Unit)
        } catch (e: FirebaseAuthUserCollisionException) {
            Result.failure(CredentialAlreadyInUseException(cause = e))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun linkOrSignInWithApple(idToken: String, rawNonce: String): Result<AuthService.AppleLinkResult> {
        // iOS resolves an Apple collision natively: the rotated credential Firebase issues for
        // the retry only exists on the raw NSError (GitLive's exception mapping drops it), and
        // without it the spent single-use token would be re-sent and rejected with 17094.
        linkOrSignInWithAppleNatively(idToken, rawNonce)?.let { return it }
        return super.linkOrSignInWithApple(idToken, rawNonce)
    }

    override suspend fun signInWithApple(idToken: String, rawNonce: String): Result<String> {
        return try {
            val result = auth.signInWithCredential(OAuthProvider.credential(providerId = "apple.com", idToken = idToken, rawNonce = rawNonce))
            val uid = result.user?.uid
                ?: return Result.failure(IllegalStateException("Sign-in returned no user"))
            Result.success(uid)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
